import * as React from 'react'
import type { CSSProperties } from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'

import { cn } from '@/lib/utils'

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal
export const DropdownMenuSub = DropdownMenuPrimitive.Sub
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, style, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    style={{ ...styles.item, ...style }}
    className={cn('dropdown-item', className)}
    {...props}
  >
    {children}
    <ChevronRight style={styles.chevron} />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, style, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    style={{ ...styles.content, ...style }}
    className={cn('dropdown-content', className)}
    {...props}
  />
))
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, style, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      style={{ ...styles.content, ...style }}
      className={cn('dropdown-content', className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }
>(({ className, inset, style, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    style={{
      ...styles.item,
      paddingLeft: inset ? '32px' : '8px',
      ...style,
    }}
    className={cn('dropdown-item', className)}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, style, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    style={{ ...styles.checkboxItem, ...style }}
    className={cn('dropdown-item', className)}
    checked={checked}
    {...props}
  >
    <span style={styles.indicator}>
      <DropdownMenuPrimitive.ItemIndicator>
        <Check style={{ width: '16px', height: '16px' }} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, style, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    style={{ ...styles.checkboxItem, ...style }}
    className={cn('dropdown-item', className)}
    {...props}
  >
    <span style={styles.indicator}>
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle style={{ width: '8px', height: '8px', fill: 'currentColor' }} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }
>(({ className, inset, style, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    style={{
      ...styles.label,
      paddingLeft: inset ? '32px' : '8px',
      ...style,
    }}
    className={className}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, style, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    style={{ ...styles.separator, ...style }}
    className={className}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const styles: Record<string, CSSProperties> = {
  content: {
    zIndex: 50,
    minWidth: '160px',
    overflow: 'hidden',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '4px',
    color: 'var(--text-primary)',
    boxShadow: 'var(--shadow-md)',
  },
  item: {
    position: 'relative',
    display: 'flex',
    cursor: 'default',
    userSelect: 'none',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'background var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
  },
  checkboxItem: {
    position: 'relative',
    display: 'flex',
    cursor: 'default',
    userSelect: 'none',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 8px 6px 32px',
    fontSize: '14px',
    outline: 'none',
    transition: 'background var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
  },
  indicator: {
    position: 'absolute',
    left: '8px',
    display: 'flex',
    width: '14px',
    height: '14px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    marginLeft: 'auto',
    width: '16px',
    height: '16px',
  },
  label: {
    padding: '6px 8px',
    fontSize: '14px',
    fontWeight: 600,
  },
  separator: {
    margin: '4px -4px',
    height: '1px',
    background: 'var(--border-default)',
  },
}
